import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import api from "../../lib/api";
import { MonthGrid, shiftMonth, monthLabel } from "../programming/MonthGrid";

const PALETTE = ["#2F5D50", "#B5854B", "#4A6FA5", "#A04848", "#6B5B95", "#3E8E7E", "#C06C35", "#5A6B3B", "#8E5A7A", "#2C3E50"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const hour = (h) => `${(h % 12) || 12}${h < 12 ? " a.m." : " p.m."}`;

export function NewsletterCalendar({ onOpen }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/newsletters/calendar", { params: { month } }).then(({ data: d }) => setData(d)).catch(() => setData({ items: [], locations: [] })); }, [month]);
  const color = (loc) => PALETTE[Math.max(0, (data?.locations || []).indexOf(loc)) % PALETTE.length];
  const byDay = {};
  (data?.items || []).forEach((it) => { (byDay[it.day] = byDay[it.day] || []).push(it); });
  return (
    <div data-testid="nl-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="nl-cal-prev" className="p-1.5 rounded hover:bg-brand-hover"><ChevronLeft className="h-4 w-4" /></button>
          <p className="font-serif text-2xl text-brand-ink min-w-[180px] text-center" data-testid="nl-cal-month">{monthLabel(month)}</p>
          <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="nl-cal-next" className="p-1.5 rounded hover:bg-brand-hover"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1" data-testid="nl-cal-legend">
          {(data?.locations || []).map((l) => <span key={l} className="inline-flex items-center gap-1.5 text-[11px] text-brand-muted"><span className="h-2.5 w-2.5 rounded-full" style={{ background: color(l) }} />{l}</span>)}
        </div>
      </div>
      <MonthGrid month={month} renderDay={(day) => (byDay[day] || []).map((it) => (
        <button key={it.id} onClick={() => onOpen(it.id)} data-testid={`nl-cal-item-${it.id}`} title={`${it.name} · ${it.subject || ""}`}
          className={`w-full text-left text-[11px] leading-tight rounded px-1.5 py-1 text-white truncate ${it.state === "ready" ? "opacity-70 ring-1 ring-dashed ring-white" : ""}`}
          style={{ background: color(it.locations[0]) }}>
          {it.state === "ready" ? "Ready · " : it.state === "scheduled" ? "Sched · " : ""}{it.name}{it.open_rate != null ? ` · ${it.open_rate}%` : ""}
        </button>
      ))} />
    </div>
  );
}

export function BestTimeCard({ location }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/newsletters/best-time", { params: { location } }).then(({ data }) => setD(data)).catch(() => setD(null)); }, [location]);
  if (!d || !d.sends) return null;
  const maxDay = Math.max(...d.by_day.map((x) => x.open_rate));
  return (
    <div className="bg-brand-surface border border-brand-border rounded-xl p-5" data-testid="nl-best-time">
      <div className="flex items-center gap-2 mb-3"><Clock className="h-4 w-4 text-brand-ink" /><h3 className="text-brand-ink font-medium">Best send time</h3>
        <span className="text-xs text-brand-tertiary">from {d.sends} sends · avg open {d.avg_open_rate}% · Eastern time</span></div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          {d.slots.length ? d.slots.map((s, i) => (
            <div key={i} className="flex items-center justify-between gap-3" data-testid={`nl-best-slot-${i}`}>
              <p className="text-sm text-brand-ink"><span className="font-mono text-brand-tertiary mr-2">{i + 1}</span>{DAYS[s.weekday]} at {hour(s.hour)}</p>
              <p className="text-sm font-mono text-brand-pos">{s.open_rate}% <span className="text-brand-tertiary text-[11px]">({s.sends} sends)</span></p>
            </div>
          )) : <p className="text-sm text-brand-tertiary">Not enough sends at any one time yet (needs 3+).</p>}
        </div>
        <div className="flex items-end gap-2 h-24" data-testid="nl-best-days">
          {d.by_day.map((x) => (
            <div key={x.weekday} className="flex-1 flex flex-col items-center gap-1" title={`${x.sends} sends`}>
              <span className="text-[10px] font-mono text-brand-muted">{x.open_rate}%</span>
              <div className={`w-full rounded-t ${x.open_rate === maxDay ? "bg-brand-primary" : "bg-brand-border"}`} style={{ height: `${Math.max(8, (x.open_rate / maxDay) * 60)}px` }} />
              <span className="text-[10px] text-brand-tertiary">{DAYS[x.weekday].slice(0, 3)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
